import type { SnakeCase } from "type-fest";
import type { ZodType } from "zod";
import z from "zod";

import {
  OrganisationIdSchema,
  OrganisationTypeSchema,
  type OrganisationIdUnbranded,
  type OrganisationType,
} from "../organisation.js";
import {
  LanguageValues,
  OfferedInstrumentVersionValues,
  VocabularyPackValues,
  type Language,
  type OfferedInstrumentVersion,
  type VocabularyPack,
} from "./catalogue.js";
import {
  SettingHolderSchema,
  type SettingHolderUnbranded,
} from "./resolution.js";
import {
  ConfigurationScopeIdSchema,
  ConfigurationScopeNameSchema,
  ConfigurationScopeSchema,
  type ConfigurationScopeIdUnbranded,
  type ConfigurationScopeUnbranded,
} from "./scope.js";
import {
  settingKeys,
  type Configuration,
  type OverridePolicy,
  type SettingKey,
  type SettingKind,
} from "./settings.js";

/**
 * The configuration that applies, as the application reads it.
 *
 * Written out field by field rather than derived from `Configuration`, because
 * tsoa builds the API contract by reading TypeScript declarations and cannot
 * follow a mapped type whose values come from a conditional one. The two are
 * held together by the schema below satisfying both: a setting added to the
 * registry and not to this shape stops compiling here.
 */
export type EffectiveConfigurationUnbranded = {
  recordMergeEnabled: boolean;
  readsApprovedAssessmentsThroughMembers: boolean;
  availableInstrumentVersions: OfferedInstrumentVersion[];
  reassessmentCarryOverDays: number;
  availableLanguages: Language[];
  defaultLanguage: Language;
  vocabularyPack: VocabularyPack;
};

export const EffectiveConfigurationSchema = z.object({
  recordMergeEnabled: z.boolean(),
  readsApprovedAssessmentsThroughMembers: z.boolean(),
  availableInstrumentVersions: z.array(z.enum(OfferedInstrumentVersionValues)),
  reassessmentCarryOverDays: z.number().int().nonnegative(),
  availableLanguages: z.array(z.enum(LanguageValues)),
  defaultLanguage: z.enum(LanguageValues),
  vocabularyPack: z.enum(VocabularyPackValues),
}) satisfies ZodType<EffectiveConfigurationUnbranded> satisfies ZodType<Configuration>;

export type EffectiveConfiguration = z.infer<
  typeof EffectiveConfigurationSchema
>;

/**
 * A value as any setting may hold it, on the wire.
 *
 * One union rather than a shape per setting: an administrative editor works over
 * the registry, so it handles whichever value kind the key it is rendering
 * declares. What narrows it is `parseSettingValue`, against the declaration for
 * the key — which is the only place a value is admitted.
 */
export type SettingValueJson = boolean | number | string | string[];

export const SettingValueJsonSchema = z.union([
  z.boolean(),
  z.number(),
  z.string(),
  z.array(z.string()),
]) satisfies ZodType<SettingValueJson>;

/**
 * One setting as the administration page needs it: what it is, what this holder
 * says about it, what actually applies, and where that came from.
 *
 * The declaration travels with the value so the page renders a control from it
 * rather than from a hand-written field per key — which is what stops the page
 * growing a control, and an inherited-value summary, per setting, kept in step
 * with each other by hand.
 *
 * `stated` is null where this holder says nothing and inherits. `inherited` is
 * what it would get if it went on saying nothing, which is what clearing reverts
 * to — so the page can offer that without refetching or re-folding anything.
 *
 * `value` is what the body actually gets, reconciled: an administration page that
 * showed the raw resolved value could show a default language the body does not
 * offer, which is the one thing a page headed "what applies" must not do. The two
 * ways a value can differ from what a layer asked for are carried separately,
 * because the reasons differ and only the reason is actionable — `clampedFrom`
 * where a layer above allowed less, `reconciledFrom` where another setting made
 * this one impossible.
 */
export type SettingViewUnbranded = {
  key: string;
  kind: SettingKind;
  valueKind: "boolean" | "whole_number" | "choice" | "choice_set";
  choices: string[];
  /** The bounds of a whole number, and absent for every other kind. */
  range?: { minimum: number; maximum: number };
  override: OverridePolicy;
  stated: SettingValueJson | null;
  value: SettingValueJson;
  source: SettingHolderUnbranded;
  inherited: SettingValueJson;
  clampedFrom?: SettingValueJson;
  reconciledFrom?: SettingValueJson;
};

export const SettingViewSchema = z.object({
  key: z.enum(settingKeys),
  kind: z.enum(["entitlement", "catalogue"]),
  valueKind: z.enum([
    "boolean",
    "whole_number",
    "choice",
    "choice_set",
  ]) satisfies ZodType<SnakeCase<SettingViewUnbranded["valueKind"]>>,
  choices: z.array(z.string()),
  range: z
    .object({ minimum: z.number().int(), maximum: z.number().int() })
    .optional(),
  override: z.enum(["free", "narrowing"]),
  stated: SettingValueJsonSchema.nullable(),
  value: SettingValueJsonSchema,
  source: SettingHolderSchema,
  inherited: SettingValueJsonSchema,
  clampedFrom: SettingValueJsonSchema.optional(),
  reconciledFrom: SettingValueJsonSchema.optional(),
}) satisfies ZodType<SettingViewUnbranded>;

export type SettingView = z.infer<typeof SettingViewSchema>;

/**
 * Everything the administration page shows about one holder: the chain it sits
 * in, outermost first, and every setting resolved through it.
 *
 * The chain is carried rather than left to the page to assemble, because the
 * order settings are folded in is the server's to state — a page that rebuilt it
 * would be the fold implemented a second time, free to disagree with the
 * server.
 */
export type HolderConfigurationUnbranded = {
  holder: SettingHolderUnbranded;
  chain: SettingHolderUnbranded[];
  settings: SettingViewUnbranded[];
};

export const HolderConfigurationSchema = z.object({
  holder: SettingHolderSchema,
  chain: z.array(SettingHolderSchema),
  settings: z.array(SettingViewSchema),
}) satisfies ZodType<HolderConfigurationUnbranded>;

export type HolderConfiguration = z.infer<typeof HolderConfigurationSchema>;

/** Every scope there is, with the organisations attached to each. */
export type ConfigurationScopeSummaryUnbranded = ConfigurationScopeUnbranded & {
  organisationCount: number;
};

export const ConfigurationScopeSummarySchema = z.object({
  id: ConfigurationScopeSchema.shape.id,
  parentScopeId: ConfigurationScopeSchema.shape.parentScopeId,
  name: ConfigurationScopeSchema.shape.name,
  organisationCount: z.number().int().nonnegative(),
}) satisfies ZodType<ConfigurationScopeSummaryUnbranded>;

export type ConfigurationScopeSummary = z.infer<
  typeof ConfigurationScopeSummarySchema
>;

export type ConfigurationScopeListUnbranded = {
  scopes: ConfigurationScopeSummaryUnbranded[];
};

export const ConfigurationScopeListSchema = z.object({
  scopes: z.array(ConfigurationScopeSummarySchema),
}) satisfies ZodType<ConfigurationScopeListUnbranded>;

export type ConfigurationScopeList = z.infer<
  typeof ConfigurationScopeListSchema
>;

/**
 * Creating or renaming a scope, and choosing what it sits under.
 *
 * `parentScopeId` is nullable and required rather than optional: a request that
 * omits it has not said whether the scope is a root, and defaulting that would
 * make "move to the top" indistinguishable from "leave where it is".
 */
export type WriteConfigurationScopeRequestUnbranded = {
  name: string;
  parentScopeId: ConfigurationScopeIdUnbranded | null;
};

export const WriteConfigurationScopeRequestSchema = z
  .object({
    name: ConfigurationScopeNameSchema,
    parentScopeId: ConfigurationScopeSchema.shape.parentScopeId,
  })
  .brand<"haven-common:WriteConfigurationScopeRequest">() satisfies ZodType<WriteConfigurationScopeRequestUnbranded>;

export type WriteConfigurationScopeRequest = z.infer<
  typeof WriteConfigurationScopeRequestSchema
>;

/** Attaching an organisation to a scope, or detaching it from every one. */
export type AttachOrganisationRequestUnbranded = {
  configurationScopeId: ConfigurationScopeIdUnbranded | null;
};

export const AttachOrganisationRequestSchema = z
  .object({
    configurationScopeId: ConfigurationScopeSchema.shape.parentScopeId,
  })
  .brand<"haven-common:AttachOrganisationRequest">() satisfies ZodType<AttachOrganisationRequestUnbranded>;

export type AttachOrganisationRequest = z.infer<
  typeof AttachOrganisationRequestSchema
>;

/** Setting one value at one holder. The key is in the path; this is the value. */
export type SetSettingRequestUnbranded = {
  value: SettingValueJson;
};

export const SetSettingRequestSchema = z
  .object({
    value: SettingValueJsonSchema,
  })
  .brand<"haven-common:SetSettingRequest">() satisfies ZodType<SetSettingRequestUnbranded>;

export type SetSettingRequest = z.infer<typeof SetSettingRequestSchema>;

/** One change in a set: a value to state, or stopping stating one. */
export type SettingChangeUnbranded =
  | { kind: "set"; key: SettingKey; value: SettingValueJson }
  | { kind: "clear"; key: SettingKey };

export const SettingChangeSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("set"),
    key: z.enum(settingKeys),
    value: SettingValueJsonSchema,
  }),
  z.object({ kind: z.literal("clear"), key: z.enum(settingKeys) }),
]) satisfies ZodType<SettingChangeUnbranded>;

export type SettingChange = z.infer<typeof SettingChangeSchema>;

/**
 * Several settings changed at one holder as one change, so the configuration
 * they leave behind is checked as a whole. A setting is named once, or which of
 * its values wins would depend on the order sent.
 */
export type ChangeSettingsRequestUnbranded = {
  changes: SettingChangeUnbranded[];
};

export const ChangeSettingsRequestSchema = z
  .object({
    changes: z
      .array(SettingChangeSchema)
      .min(1, "admin.configuration.settings.nothingChanged")
      .refine(
        (changes) =>
          new Set(changes.map((change) => change.key)).size === changes.length,
        "admin.configuration.settings.namedTwice",
      ),
  })
  .brand<"haven-common:ChangeSettingsRequest">() satisfies ZodType<ChangeSettingsRequestUnbranded>;

export type ChangeSettingsRequest = z.infer<typeof ChangeSettingsRequestSchema>;

/**
 * How many organisations one change may set to configurations. The change runs
 * in one transaction, a few queries per organisation, so an unbounded list would
 * hold its locks for as long as the sender liked.
 */
export const ORGANISATION_SCOPE_CHANGES_LIMIT = 500;

/**
 * Setting several organisations to configurations as one change. An
 * organisation is named once, for the same reason a setting is.
 */
export type AttachOrganisationsRequestUnbranded = {
  organisations: {
    organisationId: OrganisationIdUnbranded;
    configurationScopeId: ConfigurationScopeIdUnbranded | null;
  }[];
};

export const AttachOrganisationsRequestSchema = z
  .object({
    organisations: z
      .array(
        z.object({
          organisationId: OrganisationIdSchema,
          configurationScopeId: ConfigurationScopeIdSchema.nullable(),
        }),
      )
      .min(1, "admin.configuration.organisation.nothingChanged")
      .max(
        ORGANISATION_SCOPE_CHANGES_LIMIT,
        "admin.configuration.organisation.tooMany",
      )
      .refine(
        (organisations) =>
          new Set(organisations.map((each) => each.organisationId)).size ===
          organisations.length,
        "admin.configuration.organisation.namedTwice",
      ),
  })
  .brand<"haven-common:AttachOrganisationsRequest">() satisfies ZodType<AttachOrganisationsRequestUnbranded>;

export type AttachOrganisationsRequest = z.infer<
  typeof AttachOrganisationsRequestSchema
>;

/**
 * An organisation as the administration page lists it, with the scope it sits
 * under and the organisations it is a member of, so the two hierarchies can be
 * told apart in one row.
 */
export type OrganisationScopeUnbranded = {
  organisationId: OrganisationIdUnbranded;
  organisationName: string;
  organisationType: OrganisationType;
  memberOfNames: string[];
  configurationScopeId: ConfigurationScopeIdUnbranded | null;
  configurationScopeName: string | null;
};

export const OrganisationScopeSchema = z.object({
  organisationId: OrganisationIdSchema,
  organisationName: z.string().min(1).max(255),
  organisationType: OrganisationTypeSchema,
  memberOfNames: z.array(z.string()),
  configurationScopeId: ConfigurationScopeSchema.shape.parentScopeId,
  configurationScopeName: z.string().min(1).max(255).nullable(),
}) satisfies ZodType<OrganisationScopeUnbranded>;

export type OrganisationScope = z.infer<typeof OrganisationScopeSchema>;

export type OrganisationScopeListUnbranded = {
  results: OrganisationScopeUnbranded[];
  total: number;
};

export const OrganisationScopeListSchema = z.object({
  results: z.array(OrganisationScopeSchema),
  total: z.number().int().nonnegative(),
}) satisfies ZodType<OrganisationScopeListUnbranded>;

export type OrganisationScopeList = z.infer<typeof OrganisationScopeListSchema>;
